import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ConsentDocument, downloadPdf, enrollmentApi } from "./enrollmentApi";

interface DocumentReviewDialogProps {
  /** The document to show; null closes the dialog. */
  document: Pick<ConsentDocument, "artifactId" | "title" | "documentId" | "version"> | null;
  onClose: () => void;
}

/**
 * The full text of one reviewed document, exactly as frozen for signing (package spec §3).
 *
 * The HTML is shown in a sandboxed frame with scripts off: it is the server's own render, but it is
 * a complete document with its own stylesheet, and a frame keeps that from leaking into the app.
 * There is deliberately no "scroll to the bottom to continue" gate — the spec rejects it as both
 * inaccessible and meaningless. Download and Print are always available.
 */
export default function DocumentReviewDialog({ document, onClose }: DocumentReviewDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!document) return;
    let cancelled = false;
    setHtml(null);
    setFailed(false);
    enrollmentApi
      .document(document.artifactId)
      .then((doc) => {
        if (!cancelled) setHtml(doc.html ?? "");
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [document]);

  const titleId = "document-review-title";

  return (
    <Dialog
      open={!!document}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>{document?.title}</DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: "flex", minHeight: { xs: "60vh", sm: "65vh" } }}>
        {failed && (
          <Alert severity="error" sx={{ m: 2 }}>
            We could not load this document. Close and try again, or contact us.
          </Alert>
        )}
        {!failed && html === null && (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress aria-label="Loading document" />
          </Box>
        )}
        {!failed && html !== null && (
          <Box
            component="iframe"
            ref={frameRef}
            title={document?.title ?? "Document"}
            srcDoc={html}
            // Same-origin so Print can reach the frame; no allow-scripts, so nothing in it runs.
            sandbox="allow-same-origin allow-modals"
            // White in every theme: the document's own HTML is authored as dark text on paper.
            sx={{ flex: 1, border: 0, width: "100%", minHeight: { xs: "60vh", sm: "65vh" }, bgcolor: "common.white" }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button
          disabled={!document}
          onClick={() =>
            document &&
            downloadPdf(`/enrollment/documents/${document.artifactId}/pdf`, `${document.documentId}-${document.version}.pdf`)
          }
        >
          Download PDF
        </Button>
        <Button disabled={html === null} onClick={() => frameRef.current?.contentWindow?.print()}>
          Print
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
